import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageHead from "./../general/PageHead";
import KpiTile from "./../general/KpiTile";
import MapComponent from "./../maps/MapComponent";
import { authStorage } from "../../utils/storage";
import {
  Building2, Briefcase, LogOut, Users, DollarSign,
  RefreshCw, MapPin, FolderOpen, X, RefreshCcw
} from 'lucide-react';
import "./../styles/metrics/Metrics.css";



function MetricsPage() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState({
    bargainingUnitsCount: 0, totalActiveJobs: 0, totalOptOuts: 0,
    peopleContributors: 0, totalDuesPayers: 0, totalRecommitsSaves: 0,
    UnassignedUnits: 0, totalTowns: 0, totalEmployees: 0,
    recentActivityCount: 0, unitsByState: {}, complianceRate: 0,
    totalUploadedFiles: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [selectedTown, setSelectedTown] = useState(null);
  const [townUnits, setTownUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  useEffect(() => {
    const token = authStorage.getToken();
    if (!token) { navigate("/login"); return; }

    setIsLoading(true);
    fetch("/api/metrics", {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    })
      .then(r => { if (r.status === 401) { authStorage.clearAuthData(); navigate("/login"); throw new Error("Unauthorized"); } return r.json(); })
      .then(data => {
        setMetrics({
          bargainingUnitsCount: data.bargainingUnitsCount || 0,
          totalActiveJobs: data.totalActiveJobs || 0,
          totalOptOuts: data.totalOptOuts || 0,
          peopleContributors: data.peopleContributors || 0,
          totalDuesPayers: data.totalDuesPayers || 0,
          totalRecommitsSaves: data.totalRecommitsSaves || 0,
          UnassignedUnits: data.UnassignedUnits || 0,
          totalTowns: data.totalTowns || 0,
          totalEmployees: data.totalEmployees || 0,
          recentActivityCount: data.recentActivityCount || 0,
          unitsByState: data.unitsByState || {},
          complianceRate: data.complianceRate || 0,
          totalUploadedFiles: data.totalUploadedFiles || 0
        });
        setIsLoading(false);
      })
      .catch(err => { console.error(err); setIsLoading(false); });
  }, [navigate]);

  const handleTownClick = async (town) => {
    setSelectedTown(town);
    setShowUnitsModal(true);
    setUnitsLoading(true);
    const token = authStorage.getToken();
    try {
      const r = await fetch(`/api/units-by-town?town=${encodeURIComponent(town || '')}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      setTownUnits(r.ok ? await r.json() : []);
    } catch { setTownUnits([]); }
    finally { setUnitsLoading(false); }
  };

  const closeModal = () => { setShowUnitsModal(false); setSelectedTown(null); setTownUnits([]); };

  const kpis = [
    { icon: Building2,   label: "Bargaining Units",    value: metrics.bargainingUnitsCount, sub: "Total number of bargaining units",  onClick: () => navigate('/units') },
    { icon: Briefcase,   label: "Active Jobs",          value: metrics.totalActiveJobs,      sub: "Current active jobs in the system" },
    { icon: LogOut,      label: "Opt Outs",             value: metrics.totalOptOuts,          sub: "Members who have opted out" },
    { icon: Users,       label: "PEOPLE Contributors",  value: metrics.peopleContributors,    sub: "Total PEOPLE contributors" },
    { icon: DollarSign,  label: "Dues Payers",          value: metrics.totalDuesPayers,       sub: "Total active dues payers" },
    { icon: RefreshCw,   label: "Recommits / Saves",    value: metrics.totalRecommitsSaves,   sub: "Total recommitments and saves" },
    { icon: MapPin,      label: "Unassigned Units",     value: metrics.UnassignedUnits,       sub: "Units requiring assignment",        onClick: () => navigate('/unit-management') },
    { icon: FolderOpen,  label: "Uploaded Files",       value: metrics.totalUploadedFiles,    sub: "Total documents uploaded" },
  ];

  const topTowns = Object.entries(metrics.unitsByState)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  return (
    <>
      <PageHead
        title="Dashboard"
        sub="Real-time organizational insights across all locals"
        actions={
          <button className="btn btn-secondary" onClick={() => window.location.reload()}><RefreshCcw size={15} /> Refresh</button>
        }
      />

      <div className="stack-lg metrics-page">
        {/* KPI grid */}
        <div>
          <h3 style={{ marginBottom: 14, fontSize: 13.5, fontWeight: 600, color: 'var(--fg-2)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Key performance indicators</h3>
          <div className="grid-4">
            {kpis.map((k, i) => (
              <KpiTile
                key={i}
                icon={k.icon}
                label={k.label}
                value={isLoading ? '—' : (k.value ?? 0).toLocaleString()}
                sub={k.sub}
                delta={k.delta}
                deltaDir={k.deltaDir}
                onClick={k.onClick}
              />
            ))}
          </div>
        </div>

        {/* Map + Top towns */}
        <div className="grid-2-3">
          <div className="card">
            <div className="card-head">
              <h3>Coverage map</h3>
              <div className="row-end tiny muted">{isLoading ? '—' : metrics.totalTowns} towns &middot; {isLoading ? '—' : metrics.bargainingUnitsCount} units</div>
            </div>
            <div style={{ overflow: 'hidden', borderRadius: '0 0 var(--radius-lg) var(--radius-lg)' }}>
              <MapComponent center={[41.5, -72.7]} zoom={8} height="320px" />
            </div>
          </div>

          <div className="card">
            <div className="card-head"><h3>Top towns by unit count</h3></div>
            <div className="card-body" style={{ padding: 0 }}>
              {topTowns.length === 0 && isLoading && (
                <div className="empty-state" style={{ padding: 24 }}><span style={{ color: 'var(--fg-3)' }}>Loading…</span></div>
              )}
              {topTowns.map(([town, count], i) => (
                <div
                  key={town}
                  style={{ display: 'flex', alignItems: 'center', padding: '12px 22px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                  onClick={() => handleTownClick(town || null)}
                >
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--bg-accent-soft)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, marginRight: 12, flexShrink: 0 }}>
                    {i + 1}
                  </div>
                  <span style={{ flex: 1, fontWeight: 500 }}>{town || 'Unknown'}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--fg-2)' }}>{count} units</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Town units modal */}
      {showUnitsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(14,20,34,0.4)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={closeModal}>
          <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-xl)', width: '100%', maxWidth: 640, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>{selectedTown || 'Unknown'} — Units</h3>
              <button className="btn-icon" onClick={closeModal}><X size={18} /></button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {unitsLoading ? (
                <div className="empty-state"><p style={{ color: 'var(--fg-2)' }}>Loading…</p></div>
              ) : townUnits.length > 0 ? (
                <div className="table-wrap">
                  <table className="tbl">
                    <thead><tr><th>Name</th><th>Local</th><th>Unit</th><th></th></tr></thead>
                    <tbody>
                      {townUnits.map(unit => (
                        <tr key={unit.id} className="clickable" onClick={() => navigate(`/unit-info/${unit.id}`)}>
                          <td className="card-title" style={{ fontWeight: 500 }}>{unit.NAME}</td>
                          <td className="mono" data-label="Local">{unit.LOCAL}</td>
                          <td data-label="Unit">{unit.UNIT}</td>
                          <td className="row-chevron" style={{ color: 'var(--fg-link)', fontSize: 13 }}>View →</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state"><p>No units found for this town.</p></div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MetricsPage;
